import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Input, Modal, Spin } from "antd";
import { useSnackbar } from "notistack";
import { sendSubscriptionEmail } from "../../utils/organization";

const SendXeroSubscriptionEmailModal = ({
  modalStatus,
  setModalStatus,
  organizationId,
  organizationName,
  initialEmail = "",
  provider = "xero",
  onModalClose,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(initialEmail || "");

  const isQbo = provider === "qbo";
  const providerLabel = isQbo ? "QuickBooks" : "Xero";

  useEffect(() => {
    if (modalStatus) {
      setEmail(initialEmail || "");
    }
  }, [modalStatus, initialEmail]);

  const handleSend = async () => {
    const trimmed = (email || "").trim();
    if (!trimmed) {
      enqueueSnackbar("Please enter an email address.", { variant: "warning" });
      return;
    }

    setLoading(true);
    try {
      const token = await getIdTokenClaims();
      const payload = {
        email: trimmed,
        account: organizationName,
        provider: isQbo ? "qbo" : "xero",
      };
      if (isQbo) {
        payload.qbo_id = organizationId;
      } else {
        payload.xero_id = organizationId;
      }

      const response = await sendSubscriptionEmail(token.__raw, payload);

      if (response.data?.success) {
        enqueueSnackbar(
          response.data.message || "Subscription email sent.",
          { variant: "success" }
        );
        setModalStatus(false);
        onModalClose?.();
      } else {
        enqueueSnackbar(
          response.data?.message || "Failed to send subscription email.",
          { variant: "error" }
        );
      }
    } catch (err) {
      console.error(`Error sending ${providerLabel} subscription email:`, err);
      enqueueSnackbar(
        err?.response?.data?.message ||
          "An error occurred while sending the email.",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<div className="text-2xl">Send {providerLabel} subscription email</div>}
      centered
      open={modalStatus}
      footer={[
        <div className="flex justify-end gap-3" key="footer">
          <div
            className="px-2 text-sm py-1.5 border border-gray-200 rounded-lg cursor-pointer"
            onClick={() => {
              setModalStatus(false);
              onModalClose?.();
            }}
          >
            Cancel
          </div>
          <div
            className={`px-2 text-sm py-1.5 bg-blue-500 text-white rounded-lg cursor-pointer flex items-center gap-2 ${
              loading ? "opacity-75 cursor-not-allowed" : ""
            }`}
            onClick={!loading ? handleSend : undefined}
          >
            {loading && (
              <Spin
                size="small"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            )}
            Send email
          </div>
        </div>,
      ]}
      onCancel={() => {
        setModalStatus(false);
        onModalClose?.();
      }}
    >
      <div className="py-6 text-gray-500 text-sm border-b border-gray-300 space-y-4">
        <div>
          Send a subscription checkout link for
          <b className="ml-1">{organizationName || "this organization"}</b>.
          The recipient will be prompted to subscribe (or activate their 30-day free trial if eligible) and reconnect {providerLabel} to refresh tokens and resume backups.
        </div>
        <div>
          <label className="block text-gray-700 mb-1.5">Recipient email</label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="customer@example.com"
            disabled={loading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SendXeroSubscriptionEmailModal;
